import type { App } from '@grafikr/components';
import header from 'layout/header';
import collectionSpot from 'pages/collection/spot';
import productOption from 'pages/product/option';

import backButton from 'components/back-button';
import geoPopup from 'components/geo-popup';
import pagination from 'components/pagination';
import sticky from 'components/sticky';

import hasUrlParam from 'utils/loaders/has-url-param';
import intersectionLoader from 'utils/loaders/intersection';
import onAsyncInteraction from 'utils/loaders/on-async-interaction';
import onElementEvent from 'utils/loaders/on-element-event';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onEvent from 'utils/loaders/on-event';

export default (app: App) => {
  app.add({
    // Layout
    header,
    sticky,
    'geo-popup': geoPopup,
    'collection-spot': collectionSpot,
    'back-button': backButton,
    'navigation-menu': [
      intersectionLoader,
      () => import(/* webpackChunkName: "layout-navigation-menu" */ 'layout/navigation-menu'),
    ],
    'navigation-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "layout-navigation-drawer" */ 'layout/navigation-drawer'),
    ],

    // Components
    'swap-content': [
      onElementEvent,
      () => import(/* webpackChunkName: "component-swap-content" */ 'components/swap-content'),
    ],
    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    'cart-drawer': [
      [onEvent, onElementInteraction, hasUrlParam],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/cart-drawer'),
    ],
    'cart-count': [
      onEvent,
      () => import(/* webpackChunkName: "component-cart-count" */ 'components/cart-count'),
    ],
    'cart-total': [
      onEvent,
      () => import(/* webpackChunkName: "component-cart-total" */ 'components/cart-total'),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    pagination,
    collapsible: [
      ['click', onElementInteraction],
      () => import(/* webpackChunkName: "component-collapsible" */ 'components/collapsible'),
    ],
    anchor: ['click', () => import(/* webpackChunkName: "component-anchor" */ 'components/anchor')],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    'price-slider': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-price-slider" */ 'components/price-slider'),
    ],
    'video-manual': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    marquee: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-marquee" */ 'components/marquee'),
    ],
    'hotspot-grid': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-hotspot-grid" */ 'components/hotspot-grid'),
    ],
    script: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-script" */ 'components/script'),
    ],
    'script-event': [
      onEvent,
      () => import(/* webpackChunkName: "component-script" */ 'components/script'),
    ],
    'back-in-stock': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-back-in-stock" */ 'components/back-in-stock'),
    ],
    clerk: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-clerk" */ 'components/clerk'),
    ],
    'search-suggestions-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-search-suggestions-drawer" */ 'components/reactive/search/suggestions-drawer'
        ),
    ],
    'search-suggestions': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-search-suggestions" */ 'components/reactive/search/suggestions'
        ),
    ],
    'product-linked-products': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-product-linked-products" */ 'components/product-linked-products'
        ),
    ],
    'wishlist-toggle': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-wishlist-toggle" */ 'components/wishlist/toggle'),
    ],
    'wishlist-share': [
      'click',
      () => import(/* webpackChunkName: "component-wishlist-share" */ 'components/wishlist/share'),
    ],
    'product-header-actions': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-product-header-actions" */ 'components/product-header-actions'
        ),
    ],
    'filter-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-filter-form" */ 'components/filter-form'),
    ],
    'currently-watching': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-currently-watching" */ 'components/currently-watching'
        ),
    ],

    // Pages
    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-media': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-media" */ 'pages/product/media'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-recommendations': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "product-recommendations" */ 'pages/product/recommendations'),
    ],
    'product-options': [
      'click',
      () => import(/* webpackChunkName: "product-options" */ 'pages/product/options'),
    ],
    'product-option': productOption,
    'product-bundle-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-bundle-drawer" */ 'pages/product/bundle-drawer'),
    ],
    'product-description-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "product-description-drawer" */ 'pages/product/description-drawer'
        ),
    ],
    'product-shipping-drawer': [
      onAsyncInteraction,
      () =>
        import(/* webpackChunkName: "product-shipping-drawer" */ 'pages/product/shipping-drawer'),
    ],
    'product-refund-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-refund-drawer" */ 'pages/product/refund-drawer'),
    ],
    'product-sizeguide-drawer': [
      onElementInteraction,
      () =>
        import(/* webpackChunkName: "product-sizeguide-drawer" */ 'pages/product/sizeguide-drawer'),
    ],
    'product-back-in-stock-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "product-back-in-stock-drawer" */ 'pages/product/back-in-stock-drawer'
        ),
    ],
    'product-gift-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-gift-drawer" */ 'pages/product/gift-drawer'),
    ],
    'product-property-update': [
      onEvent,
      () =>
        import(/* webpackChunkName: "product-property-update" */ 'pages/product/property-update'),
    ],
    quantity: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-quantity" */ 'components/quantity'),
    ],
    'product-delivery-date': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-delivery-date" */ 'pages/product/delivery-date'),
    ],
    'product-lightbox': [
      onAsyncInteraction,
      () => import(/* webpackChunkName: "product-lightbox" */ 'pages/product/lightbox'),
    ],

    // Collection
    'collection-sort-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "collection-sort-drawer" */ 'pages/collection/sort-drawer'),
    ],
    'collection-filter-drawer': [
      onElementInteraction,
      () =>
        import(/* webpackChunkName: "collection-filter-drawer" */ 'pages/collection/filter-drawer'),
    ],
    'collection-restriction': [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-restriction" */ 'pages/collection/restriction'),
    ],

    // Search
    search: [
      intersectionLoader,
      () => import(/* webpackChunkName: "search-main" */ 'pages/search/main'),
    ],
    'search-sort-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "search-sort-drawer" */ 'pages/search/sort-drawer'),
    ],

    // Wishlist
    wishlist: [
      intersectionLoader,
      () => import(/* webpackChunkName: "wishlist-main" */ 'pages/wishlist/main'),
    ],
  });
};
